import type { AttendanceAlert, AttendanceAnalytics, AttendanceRecord, RegisterLearner } from "../../services/attendanceService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Learner attendance: taking a register, the records it leaves, the learners
 * and classes to follow up, and alerts. Choices mirror the Attendance model in
 * apps/students/models.py; rates mirror attendance_analytics (present, late
 * and half day count as there).
 */

export type Status = AttendanceRecord["status"];

export const STATUSES: { value: Status; label: string; short: string; dot: string }[] = [
  { value: "present", label: "Present", short: "P", dot: "bg-emerald-500" },
  { value: "late", label: "Late", short: "L", dot: "bg-amber-500" },
  { value: "absent", label: "Absent", short: "A", dot: "bg-red-500" },
  { value: "excused", label: "Excused", short: "E", dot: "bg-sky-500" },
  { value: "sick", label: "Sick", short: "S", dot: "bg-orange-500" },
  { value: "half_day", label: "Half day", short: "H", dot: "bg-amber-500" },
  { value: "emergency", label: "Emergency leave", short: "Em", dot: "bg-orange-500" },
];
/** The three a register is mostly taken with; the rest sit behind "Other". */
export const QUICK_STATUSES: Status[] = ["present", "late", "absent"];
export const PERIODS: [string, string][] = [["full_day", "Whole day"], ["morning", "Morning"], ["afternoon", "Afternoon"], ["evening", "Evening"]];
export const ALERT_TYPES: [string, string][] = [["consecutive_absences", "Away several days running"], ["low_attendance", "Low attendance"], ["frequent_absences", "Frequent absences"], ["pattern_alert", "Pattern"]];
export const PRIORITIES: [string, string][] = [["critical", "Critical"], ["high", "High"], ["medium", "Medium"], ["low", "Low"]];

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);
export const statusOf = (value: string) => STATUSES.find((row) => row.value === value) ?? { value: value as Status, label: humanise(value), short: "?", dot: "bg-gray-400" };
export const statusDot = (value: string) => ({ label: statusOf(value).label, dot: statusOf(value).dot });

export const PRESENTISH: Status[] = ["present", "late", "half_day"];
export const AWAY: Status[] = ["absent", "sick", "emergency"];

/** What a rate means, in words, for a details panel beside the figure itself. */
export function rateMeaning(percentage: number) {
  if (percentage >= 90) return { label: "Good", dot: "bg-emerald-500" };
  if (percentage >= 80) return { label: "Worth watching", dot: "bg-amber-500" };
  return { label: "Needs following up", dot: "bg-red-500" };
}

/** How a rate reads: 90% and over is good, under 80% needs following up. */
export function rateStatus(percentage: number) {
  if (percentage >= 90) return { label: `${percentage.toFixed(1)}%`, dot: "bg-emerald-500" };
  if (percentage >= 80) return { label: `${percentage.toFixed(1)}%`, dot: "bg-amber-500" };
  return { label: `${percentage.toFixed(1)}%`, dot: "bg-red-500" };
}

// --- Taking a register -----------------------------------------------------------------------

export interface RegisterEntry {
  status: Status;
  remarks: string;
  /** The saved record this row came from, if the register was taken before. */
  recordId?: number;
}

/** A register as it stands: saved marks where there are some, everyone else present. */
export function registerDraft(learners: RegisterLearner[], saved: AttendanceRecord[], lesson: number | null) {
  const mine = saved.filter((row) => (lesson ? row.timetable_slot === lesson : !row.timetable_slot && row.period === "full_day"));
  const byLearner = new Map(mine.map((row) => [row.student, row]));
  return Object.fromEntries(
    learners.map((learner) => {
      const record = byLearner.get(learner.id);
      return [learner.id, { status: record?.status ?? "present", remarks: record?.remarks ?? "", recordId: record?.id } satisfies RegisterEntry];
    }),
  ) as Record<number, RegisterEntry>;
}

/** Rows that differ from what was loaded, so saving can say what it changes. */
export const changedRows = (before: Record<number, RegisterEntry>, now: Record<number, RegisterEntry>) =>
  Object.keys(now).filter((id) => before[Number(id)]?.status !== now[Number(id)].status || (before[Number(id)]?.remarks ?? "") !== now[Number(id)].remarks).length;

export function registerCounts(entries: Record<number, RegisterEntry>) {
  const counts: Record<string, number> = {};
  for (const entry of Object.values(entries)) counts[entry.status] = (counts[entry.status] ?? 0) + 1;
  return counts;
}

/** Monday 0 .. Sunday 6, as timetable slots store it, for a yyyy-mm-dd day. */
export function schoolDayOfWeek(isoDate: string) {
  const [year, month, date] = isoDate.split("-").map(Number);
  return (new Date(year, month - 1, date).getDay() + 6) % 7;
}

// --- Records ---------------------------------------------------------------------------------

export const recordConfig: RegisterConfig<AttendanceRecord> = {
  search: (row) => [row.student_name, row.student_id, row.class_name, row.remarks, row.lesson_subject_name, row.marked_by_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "present", label: "Present", match: (row) => row.status === "present" },
    { key: "late", label: "Late or half day", match: (row) => row.status === "late" || row.status === "half_day" },
    { key: "away", label: "Away", match: (row) => AWAY.includes(row.status) },
    { key: "excused", label: "Excused", match: (row) => row.status === "excused" },
  ],
  date: { label: "Date", value: (row) => row.date },
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "-date", label: "Newest first", compare: (a, b) => b.date.localeCompare(a.date) || a.student_name.localeCompare(b.student_name) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<AttendanceRecord>((row) => `${row.student_name} ${row.date}`) },
    { key: "class", label: "Class", compare: byText<AttendanceRecord>((row) => `${row.class_name} ${row.student_name}`) },
  ],
};

export const registerLabel = (row: Pick<AttendanceRecord, "lesson_subject_name" | "lesson_period_name" | "period">) =>
  row.lesson_subject_name ? `${row.lesson_subject_name}${row.lesson_period_name ? `, ${row.lesson_period_name}` : ""}` : labelFrom(PERIODS, row.period);

// --- Learners and classes to follow up -------------------------------------------------------

export type LowLearner = AttendanceAnalytics["poor_attendance_students"][number];

export const lowLearnerConfig: RegisterConfig<LowLearner> = {
  search: (row) => [row.student_name, row.student_id, row.class_name].join(" "),
  views: [
    { key: "all", label: "Below 80%" },
    { key: "serious", label: "Below 60%", match: (row) => row.percentage < 60 },
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "rate", label: "Lowest rate", compare: byNumber<LowLearner>((row) => row.percentage) },
    { key: "-away", label: "Most days away", compare: reversed(byNumber<LowLearner>((row) => row.total_days - row.present_days)) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<LowLearner>((row) => row.student_name) },
  ],
};

export type ClassRate = AttendanceAnalytics["class_statistics"][number];

export const classRateConfig: RegisterConfig<ClassRate> = {
  search: (row) => row.class_name,
  views: [
    { key: "all", label: "All classes" },
    { key: "below", label: "Below 90%", match: (row) => row.total > 0 && row.percentage < 90 },
    { key: "none", label: "No registers", match: (row) => row.total === 0 },
  ],
  sorts: [
    { key: "rate", label: "Lowest rate", compare: (a, b) => (a.total ? a.percentage : 101) - (b.total ? b.percentage : 101) },
    { key: "name", label: "Class (A–Z)", compare: byText<ClassRate>((row) => row.class_name) },
  ],
};

export type DayRate = AttendanceAnalytics["daily_trends"][number];

/** Days that had registers, newest first; days without any say nothing. */
export const daysWithRegisters = (days: DayRate[]) => days.filter((row) => row.total > 0).sort((a, b) => String(b.date).localeCompare(String(a.date)));

export const dayRateConfig: RegisterConfig<DayRate> = {
  search: (row) => String(row.date),
  views: [
    { key: "all", label: "All days" },
    { key: "below", label: "Below 90%", match: (row) => row.percentage < 90 },
  ],
  date: { label: "Date", value: (row) => String(row.date) },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<DayRate>((row) => String(row.date))) },
    { key: "rate", label: "Lowest rate", compare: byNumber<DayRate>((row) => row.percentage) },
  ],
};

// --- Alerts ----------------------------------------------------------------------------------

const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export function alertStatus(row: Pick<AttendanceAlert, "is_resolved" | "priority">) {
  if (row.is_resolved) return { label: "Resolved", dot: "bg-emerald-500" };
  return { label: `${labelFrom(PRIORITIES, row.priority)} priority`, dot: row.priority === "critical" || row.priority === "high" ? "bg-red-500" : "bg-amber-500" };
}

export const alertConfig: RegisterConfig<AttendanceAlert> = {
  search: (row) => [row.student_name, row.student_id, row.class_name, row.message, row.resolution_notes].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => !row.is_resolved },
    { key: "resolved", label: "Resolved", match: (row) => row.is_resolved },
    { key: "all", label: "All" },
  ],
  date: { label: "Raised", value: (row) => row.created_at },
  category: { label: "Kind", value: (row) => labelFrom(ALERT_TYPES, row.alert_type) },
  sorts: [
    { key: "priority", label: "Most urgent", compare: (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) || b.created_at.localeCompare(a.created_at) },
    { key: "-raised", label: "Newest first", compare: reversed(byText<AttendanceAlert>((row) => row.created_at)) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<AttendanceAlert>((row) => row.student_name) },
  ],
};
